/**
 * Council Page Calculations (SF-63)
 *
 * Client-side formula replication for Council page decomposition tabs.
 * These calculations MUST stay synchronized with backend systems:
 * - EconomySystem.calculateProduction() for gold
 * - FoodSystem for food production/consumption
 * - MagicSystem for rune production
 *
 * See InMemoryPropertySystem.java for authoritative property values.
 */

import type { Province } from '../types/province';
import {
  MONEY_PER_EMPLOYED,
  MONEY_PER_UNEMPLOYED,
  MONEY_PER_BANK,
  WAGE_PER_SOLDIER,
  WAGE_PER_SPECIALIST,
  WAGE_PER_ELITE,
  WAGE_PER_THIEF,
  HONOUR_INCOME_MOD,
  ALCHEMY_INCOME_MOD,
  PLAGUE_INCOME_MOD,
  RIOT_INCOME_MOD,
  INSPIRE_ARMY_WAGE_MOD,
  GREED_WAGE_MOD,
} from '../constants/modifierLookup';

/**
 * Gold Flow Breakdown
 */
export interface GoldFlowBreakdown {
  income: {
    employed: number;
    unemployed: number;
    base: number;
    taxBonus: number;
    bankBonus: number;
    subtotal: number;
    modifiers: {
      plague: number;
      riot: number;
      honour: number;
      alchemy: number;
      total: number;
    };
    total: number;
  };
  expenses: {
    soldiers: number;
    specialists: number;
    elites: number;
    thieves: number;
    wizards: number;
    subtotal: number;
    modifiers: {
      stance: number;
      inspireArmy: number;
      greed: number;
      total: number;
    };
    draft: number;
    science: number;
    total: number;
  };
  net: number;
}

/**
 * Calculate gold flow breakdown for a province.
 * Replicates EconomySystem.calculateProduction().
 */
export function calculateGoldFlow(province: Province): GoldFlowBreakdown {
  const { economy, territory, population, magic, sciences, metadata } = province;
  const employed = economy.employment.employed;
  const unemployed = economy.employment.unemployed;
  const banks = territory.buildings.BANK || 0;
  const taxRate = economy.taxRate;

  // =========== INCOME ===========

  const employedIncome = employed * MONEY_PER_EMPLOYED;
  const unemployedIncome = unemployed * MONEY_PER_UNEMPLOYED;
  const baseIncome = employedIncome + unemployedIncome;

  // Tax bonus (decaying land capture bonus)
  const taxBonus = baseIncome * (taxRate / 100);

  // Bank bonus (2% per bank, percentage modifier)
  const bankBonus = baseIncome * (banks * MONEY_PER_BANK);

  const subtotalIncome = baseIncome + taxBonus + bankBonus;

  // Income modifiers (match EconomySystem.getIncomeModifier)
  const hasPlague = magic.activeEffects.some((e) => e.effectType === 'PLAGUE');
  const hasRiot = magic.activeEffects.some((e) => e.effectType === 'RIOT');
  const hasAlchemy = (sciences.books.ALCHEMY || 0) > 0;

  const plagueMod = hasPlague ? PLAGUE_INCOME_MOD : 1.0;
  const riotMod = hasRiot ? RIOT_INCOME_MOD : 1.0;
  const honourMod = HONOUR_INCOME_MOD[metadata.title] || 1.0;
  const alchemyMod = hasAlchemy ? ALCHEMY_INCOME_MOD : 1.0;

  const totalIncomeMod = plagueMod * riotMod * honourMod * alchemyMod;
  const totalIncome = subtotalIncome * totalIncomeMod;

  // =========== EXPENSES ===========

  // Base wages
  const soldierWages = population.soldiers * WAGE_PER_SOLDIER;
  const specialistWages =
    (population.offenseSpecialists + population.defenseSpecialists) * WAGE_PER_SPECIALIST;
  const eliteWages = population.elites * WAGE_PER_ELITE;
  const thiefWages = population.thieves * WAGE_PER_THIEF;
  // Wizards do NOT consume wages (EconomySystem.calculateConsumption excludes them)
  const wizardWages = 0;

  const baseWages =
    soldierWages + specialistWages + eliteWages + thiefWages;

  // Wage modifiers
  // Note: kingdom.stance is undefined in current DTO, default to NEUTRAL (1.0)
  const stanceMod = 1.0; // STANCE_MILITARY_WAGE_MOD[kingdom.stance] || 1.0;
  const hasInspireArmy = magic.activeEffects.some((e) => e.effectType === 'INSPIRE_ARMY');
  const hasGreed = magic.activeEffects.some((e) => e.effectType === 'GREED');

  const inspireArmyMod = hasInspireArmy ? INSPIRE_ARMY_WAGE_MOD : 1.0;
  const greedMod = hasGreed ? GREED_WAGE_MOD : 1.0;

  const totalWageMod = stanceMod * inspireArmyMod * greedMod;
  const totalWages = baseWages * totalWageMod;

  // Draft and science expenses (from DTO lastTickChanges)
  const draftExpense = economy.lastTickChanges.draftGoldSpent;
  const scienceExpense = economy.lastTickChanges.scienceGoldSpent;

  const totalExpenses = totalWages + draftExpense + scienceExpense;

  // =========== NET ===========

  const netGold = totalIncome - totalExpenses;

  return {
    income: {
      employed: employedIncome,
      unemployed: unemployedIncome,
      base: baseIncome,
      taxBonus,
      bankBonus,
      subtotal: subtotalIncome,
      modifiers: {
        plague: plagueMod,
        riot: riotMod,
        honour: honourMod,
        alchemy: alchemyMod,
        total: totalIncomeMod,
      },
      total: totalIncome,
    },
    expenses: {
      soldiers: soldierWages,
      specialists: specialistWages,
      elites: eliteWages,
      thieves: thiefWages,
      wizards: wizardWages,
      subtotal: baseWages,
      modifiers: {
        stance: stanceMod,
        inspireArmy: inspireArmyMod,
        greed: greedMod,
        total: totalWageMod,
      },
      draft: draftExpense,
      science: scienceExpense,
      total: totalExpenses,
    },
    net: netGold,
  };
}

/**
 * Per-acre calculations (OPA, DPA, TPA, WPA).
 * Guards against division by zero.
 */
export function calculatePerAcre(value: number, acres: number): number {
  if (acres <= 0) return 0;
  return value / acres;
}
